import type { Locale } from "@/lib/i18n/config";
import { APEX_ORIGIN } from "@/lib/zones/app-routes";
import { MAX_ZONE_HOPS, ZONE_HOP_PARAM } from "@/lib/zones/hop-budget";

const MAX_LENGTH = 512;
const SCHEME_AFTER_SLASHES = /^\/+[a-z][a-z0-9+\-.]*:/i;

function hasControlChars(value: string): boolean {
	for (let i = 0; i < value.length; i++) {
		const code = value.charCodeAt(i);
		if (code <= 0x1f || code === 0x7f) return true;
	}
	return false;
}

/** Shared validation core for both `sanitizeCallbackURL` variants below. */
function sanitizePathAgainst(
	raw: string | string[] | undefined,
	fallback: string,
): string {
	const value = Array.isArray(raw) ? raw[0] : raw;
	if (typeof value !== "string" || value.length === 0) return fallback;
	if (value.length > MAX_LENGTH) return fallback;
	if (!value.startsWith("/")) return fallback;
	if (value.startsWith("//")) return fallback;
	if (value.startsWith("/\\")) return fallback;
	if (SCHEME_AFTER_SLASHES.test(value)) return fallback;
	if (hasControlChars(value)) return fallback;
	return value;
}

/**
 * Return a guaranteed same-origin path, or fall back to the locale home.
 * Rejects absolute URLs, protocol-relative URLs, and other open-redirect vectors.
 */
export function sanitizeCallbackURL(
	raw: string | string[] | undefined,
	locale: Locale,
): string {
	return sanitizePathAgainst(raw, `/${locale}/seeker`);
}

/**
 * Employer-door variant of `sanitizeCallbackURL` — same validation, default
 * fallback is the employer dashboard instead of the seeker home (HITL
 * screening PR-5, plan §3 "post-login routing"). Used by
 * `/employers/login` and `/employers/signup` only; the seeker doors are
 * untouched.
 */
export function sanitizeEmployerCallbackURL(
	raw: string | string[] | undefined,
	locale: Locale,
): string {
	return sanitizePathAgainst(raw, `/${locale}/employers/dashboard`);
}

/**
 * Business surfaces a signup can return to: the `businessCallback` targets in
 * `src/lib/recruiter/role-intent.ts` plus the developer/API pages. An
 * employer, advertiser or data buyer is never routed through the seeker CV
 * step, even when they signed up through the seeker door.
 */
const BUSINESS_PATH_RE =
	/^\/(?:id|en)\/(?:employers|advertise|data|developers)(?:[/?#]|$)/;

export function isBusinessCallbackPath(path: string): boolean {
	return BUSINESS_PATH_RE.test(path);
}

const ONBOARDING_PATH_RE = /^\/(?:id|en)\/onboarding(?:[/?#]|$)/;

/** True for `/{locale}/onboarding` with any query: the post-signup stop. */
export function isOnboardingPath(path: string): boolean {
	return ONBOARDING_PATH_RE.test(path);
}

/**
 * Route a SEEKER signup through `/{locale}/onboarding?next=<callback>`, where
 * the `signup-cv-step` split test (src/lib/onboarding/signup-cv-step.ts)
 * decides whether to ask for a CV first. With the flag absent or `control`,
 * `/onboarding` redirects straight to `next`, so the only cost is the hop.
 *
 * History: this was a pass-through from 2026-05-17 (#498), when a forced
 * profile FORM at `/onboarding` was removed because 59% bailed at the first
 * field. It routes again (owner 2026-11-05: talent-pool "Has CV" at 33%, aim
 * 90%, as a split test) because only 194 of 349 signups in 30 days ever
 * reached /seeker, where the CV ask lived.
 *
 * Pass-through, unchanged, for:
 * - `surface: "login"`: a returning user is not a new signup;
 * - `role: "business"` and any business callback path;
 * - a callback that already points at `/onboarding` (no double wrap).
 *
 * The result is a relative apex path. `/onboarding` is in `APP_ZONE_PATHS`,
 * so the apex middleware hands it to the app zone, and the app zone's page
 * names the apex directly when `next` is an apex page (no bounce, #5238).
 */
export function wrapCallbackThroughOnboarding(
	safeCallback: string,
	locale: Locale,
	opts: { surface: "signup" | "login"; role?: "seeker" | "business" | null },
): string {
	if (opts.surface !== "signup") return safeCallback;
	if (opts.role === "business") return safeCallback;
	if (isBusinessCallbackPath(safeCallback)) return safeCallback;
	if (isOnboardingPath(safeCallback)) return safeCallback;
	return `/${locale}/onboarding?next=${encodeURIComponent(safeCallback)}`;
}

/**
 * Where an unauthenticated request to a protected page should redirect:
 * `/{locale}/login?callbackURL=<path>`. The `callbackURL` round-trips through
 * `sanitizeCallbackURL` on the login page, so it both (a) drives the
 * cross-zone-context subtitle ("Sign in to continue") and (b) sends the user
 * back to `path` after they sign back in.
 *
 * Use this from any guard — on either zone — that redirects a logged-out
 * visitor to `/login`, instead of a bare `/${locale}/login` with no context.
 * Incident 2026-07-07: sessions arriving on `/login` from app.lokerdollar.com
 * with no `callbackURL` bounced 70% of the time — they had no signal for why
 * they were sent there.
 */
export function buildLoginRedirect(locale: Locale, path: string): string {
	return `/${locale}/login?callbackURL=${encodeURIComponent(path)}`;
}

/**
 * App-zone variant of `buildLoginRedirect`: an ABSOLUTE apex URL with the
 * cross-zone hop budget already spent.
 *
 * Two deliberate differences from `buildLoginRedirect`, both load-bearing for
 * the 2026-09-08 redirect-loop fix (`src/lib/zones/hop-budget.ts`):
 *
 *  1. **Absolute.** A relative `/{locale}/login` returned from an app-zone
 *     guard resolves against `app.lokerdollar.com`, where /login is not a
 *     route — so `apps/app/src/middleware.ts` bounced it to the apex, costing
 *     an extra hop and putting the visitor back on the worker that had just
 *     sent them over. Naming the apex directly removes that hop entirely.
 *  2. **Budget spent.** The app zone is the side that VALIDATES the session
 *     against D1. When it says "not signed in", that is the terminal answer,
 *     and the apex login page must render it rather than re-deciding from
 *     cookie presence and redirecting back. Stamping the budget as spent is
 *     what makes that non-negotiable instead of merely likely.
 *
 * Use from any app-zone guard. Apex-side guards keep `buildLoginRedirect` —
 * a same-origin relative path there is correct and costs no hop.
 */
export function buildApexLoginRedirect(locale: Locale, path: string): string {
	return `${APEX_ORIGIN}/${locale}/login?callbackURL=${encodeURIComponent(path)}&${ZONE_HOP_PARAM}=${MAX_ZONE_HOPS}`;
}

/**
 * App-zone variant for a non-login destination the apex owns (`/pricing`,
 * `/signup`). Same reasoning as `buildApexLoginRedirect`: name the apex
 * directly instead of emitting a relative path the zone middleware has to
 * bounce. The hop budget is NOT spent here — these are ordinary product
 * navigations, not a rejected session, and the apex page they land on is
 * terminal anyway.
 */
export function buildApexPath(locale: Locale, path: string): string {
	return `${APEX_ORIGIN}/${locale}${path}`;
}

/**
 * Reduce a same-origin path to its path segment, dropping any `?query` and
 * `#fragment`. Used when a path has to be nested inside another URL's query
 * string — see `buildMagicLinkErrorCallbackURL` for why Better Auth rejects
 * the nested form otherwise.
 */
function stripQueryAndFragment(path: string): string {
	const end = path.search(/[?#]/);
	return end === -1 ? path : path.slice(0, end);
}

/** Employer-door counterpart of `buildLoginRedirect` — `/employers/login` instead of `/login`. */
export function buildEmployerLoginRedirect(
	locale: Locale,
	path: string,
): string {
	return `/${locale}/employers/login?callbackURL=${encodeURIComponent(path)}`;
}

/**
 * `errorCallbackURL` for `signIn.magicLink()` calls: where Better Auth's
 * `/magic-link/verify` endpoint redirects when the token is invalid, expired,
 * or otherwise fails (it appends `?error=<CODE>` to whatever URL this
 * produces). Left unset, Better Auth defaults `errorCallbackURL` to
 * `callbackURL` — which is how an expired link used to strand signed-out
 * users on `/{locale}/seeker?error=INVALID_TOKEN`, a dead end nothing reads
 * (root cause candidate #2, incident 2026-07-07). Routing to `/login` instead
 * puts the failure on the one page with a banner for it, and the same
 * `MagicLinkForm` doubles as the resend affordance.
 *
 * The nested callback is reduced to its PATH — no query, no fragment — and it
 * has to be. Better Auth validates this value twice over:
 *
 *  1. `/magic-link/verify` runs `decodeURIComponent` on a value its query
 *     parser has ALREADY decoded once (better-auth
 *     `dist/plugins/magic-link/index.mjs`, the third `originCheck`), so an
 *     inner `%3F` re-emerges as a literal `?`.
 *  2. The resulting string is matched against the relative-path pattern in
 *     `dist/auth/trusted-origins.mjs` (`matchesOriginPattern`):
 *       /^\/(?!\/|\\|%2f|%5c)[\w\-.\+/@]*(?:\?[\w\-.\+/=&%@]*)?$/
 *     which admits AT MOST ONE `?`, and no `#` in either segment.
 *
 * So `/id/login?callbackURL=%2Fid%2Fpricing%3Fintent%3Dmonthly` decodes to
 * `/id/login?callbackURL=/id/pricing?intent=monthly` — two `?` — and Better
 * Auth rejects the whole verify request with INVALID_CALLBACK_URL. Because
 * every paid pricing CTA appends `?intent=monthly|quarterly|annual`, that made
 * checkout unreachable for any signed-out visitor: the magic link they were
 * sent could not be redeemed at all. Zero `checkout_intents` were recorded in
 * the 30 days to 2026-08-28 (audit `docs/audit/growth-monetization-reality-audit-2026-08-28.md`).
 *
 * The daily synthetic checkout probe never caught it because its callbacks
 * carry no query string, so it only ever exercised the one-`?` case.
 *
 * Only the ERROR path loses the query. The success `callbackURL` is passed
 * through untouched and still carries `?intent=…`, so a normal sign-in returns
 * the buyer to their selected plan. A user whose link actually expired lands on
 * `/{locale}/login` and re-requests from there.
 */
export function buildMagicLinkErrorCallbackURL(
	locale: Locale,
	callbackURL: string,
): string {
	return buildLoginRedirect(locale, stripQueryAndFragment(callbackURL));
}
